const React = require('react');
require('figmaCSS');
import '@styles/editField/TimeFrameCard.css';
const { useState, useEffect } = require('react');
const { useTranslation } = require('react-i18next');
const { SelectMenu } = require('SelectMenu');
const { unitDisplayName } = require('unitsManager');
const { getFirstDayOptions, daysArray } = require('timeFramesManager');
const { shouldWeekStartWithMonday } = require('@managers/farmingUnitManager');
const { removeDuplicateValues } = require('@managers/helpers');
const dayjs = require('../../../utils/dayjsConfig');
const {
  plannerType,
  weeklyPlannerModes,
} = require('@managers/planningManager');
const { useSelector } = require('react-redux');

export function RadioButtons({
  currentPlannerType,
  handlePlannerSettingsChange,
}) {
  const { t } = useTranslation();

  // Radio options as per Figma: No planner, By day, By week
  const options = [
    {
      value: plannerType.DISABLED,
      label: t('no_recommendation'),
      testId: 'radio-no-planner',
    },
    {
      value: plannerType.DAILY_RECOMMENDATION,
      label: t('edit_field_planner_type_by_day'),
      testId: 'radio-by-days',
    },
    {
      value: plannerType.WEEKLY_RECOMMENDATION,
      label: t('edit_field_planner_type_by_week'),
      testId: 'radio-by-weeks',
    },
  ];

  return (
    <div
      className='d-flex flex-row align-items-center border-bottom border-bottom-grey-91'
      style={{ gap: 64 }}
      data-testid='planner-state-radio-group'
    >
      {options.map((option) => (
        <label
          key={option.value}
          className='d-flex align-items-center'
          style={{ gap: 8, cursor: 'pointer', height: 32 }}
        >
          <span
            className={`radio-button-box ${currentPlannerType === option.value ? 'active' : ''}`}
          >
            <input
              type='radio'
              name='plannerType'
              value={option.value}
              checked={currentPlannerType === option.value}
              onChange={() => handlePlannerSettingsChange('type', option.value)}
              className='radio-button-input'
              data-testid={option.testId}
            />
            {currentPlannerType === option.value && (
              <span className='radio-button-active-circle' />
            )}
          </span>
          <div className='text-style-short-lables-16-regular text-grey45'>
            {option.label}
          </div>
        </label>
      ))}
    </div>
  );
}

function ByDaysPlannerSettings({
  is_dynamic_planner_active,
  handleSettingsChange,
}) {
  const { t } = useTranslation();
  return (
    <div className='form-check mt-3'>
      <input
        className='form-check-input'
        checked={is_dynamic_planner_active}
        onChange={(event) => {
          handleSettingsChange(
            'is_dynamic_planner_active',
            event.target.checked,
          );
        }}
        style={{ marginTop: '0.3rem' }}
        type='checkbox'
        id='dynamic_planner'
      />
      <div>
        <div className='text-grey45 m-0' htmlFor='dynamic_planner'>
          {t('Edit_field_planner_dynamic_update_title')}
        </div>
        <small className='text-grey71 d-block'>
          {t('Edit_field_planner_dynamic_update_description')}
        </small>
      </div>
    </div>
  );
}

function FirstDaySelector({ firstDay, onFirstDayChange, firstDayOptions }) {
  const { t } = useTranslation();

  return (
    <div style={{ width: 240 }}>
      <div
        className='text-style-short-lables-14-semi-bold text-grey45'
        style={{ marginBottom: 4, display: 'block' }}
      >
        {t('edit_field_timeframe_constraint_first_day')}
      </div>
      <SelectMenu
        options={firstDayOptions}
        curValue={firstDay}
        onChange={onFirstDayChange}
        menuStyle={{
          height: '40px',
          border: '1px solid var(--color-grey-91)',
        }}
        selectClassName={'tfedit-select-menu'}
        sortBox={'sortBox'}
      />
    </div>
  );
}

function SeasonTargetSelector({
  seasonTarget,
  onSeasonTargetChange,
  userLengthUnits,
}) {
  const { t } = useTranslation();

  const isInputValueZero = (value) => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue === 0;
  };

  const handleSelectDefaultZeroOnFocus = (event) => {
    if (isInputValueZero(event.currentTarget?.value)) {
      event.currentTarget.select();
    }
  };

  const handleSelectDefaultZeroOnClick = (event) => {
    // Only auto-select when the current value is exactly "0".
    // Clicking the native spinner typically changes the value before `click` fires,
    // so this won't interfere with steppers across browsers.
    if (isInputValueZero(event.currentTarget?.value)) {
      event.currentTarget.select();
    }
  };

  return (
    <div>
      <div
        className='text-style-short-lables-14-semi-bold text-grey45'
        style={{ marginBottom: 4, display: 'block' }}
      >
        {t('edit_field_timeframe_constraint_season_target')}{' '}
        {`(${unitDisplayName(userLengthUnits)})`}
      </div>
      <input
        type='number'
        min='0'
        className='form-control text-style-short-lables-16-regular'
        style={{ height: 40, border: '1px solid var(--color-grey-91)' }}
        value={seasonTarget}
        onChange={onSeasonTargetChange}
        onFocus={handleSelectDefaultZeroOnFocus}
        onClick={handleSelectDefaultZeroOnClick}
        placeholder='0'
      />
    </div>
  );
}

function ModeSelector({ mode, onModeChange }) {
  const { t } = useTranslation();
  return (
    <div className='d-flex flex-column'>
      <div
        className='d-flex align-items-center'
        style={{ gap: 8, cursor: 'pointer', height: 32 }}
      >
        <input
          type='radio'
          name='planner_mode_type'
          value={weeklyPlannerModes.WEEKLY}
          checked={mode === weeklyPlannerModes.WEEKLY}
          onChange={() => onModeChange(weeklyPlannerModes.WEEKLY)}
          style={{
            width: 16,
            height: 16,
            accentColor: 'var(--color-primary-idel)',
          }}
        />
        <span className='text-style-short-lables-16-regular text-grey45'>
          {t('edit_field_timeframe_constraint_weekly_mode')}
        </span>
      </div>
      <div
        className='d-flex align-items-center'
        style={{ gap: 8, cursor: 'pointer', height: 32 }}
      >
        <input
          type='radio'
          name='planner_mode_type'
          value={weeklyPlannerModes.INTERVAL}
          checked={mode === weeklyPlannerModes.INTERVAL}
          onChange={() => onModeChange(weeklyPlannerModes.INTERVAL)}
          style={{
            width: 16,
            height: 16,
            accentColor: 'var(--color-primary-idel)',
          }}
        />
        <span
          className='text-style-short-lables-16-regular'
          style={{ color: 'var(--color-grey-45)' }}
        >
          {t('edit_field_timeframe_constraint_interval_mode')}
        </span>
      </div>
    </div>
  );
}

const errorMessages = {
  first_day_of_week: 'edit_field_planner_changes_message_first_day_body',
  mode: 'edit_field_planner_changes_message_mode_body',
};

function ByWeeksPlannerSettings({
  handlePlannerSettingsChange,
  plannerSettings = {},
  userLengthUnits,
  originalPlannerSettings,
}) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    updateFieldError('first_day_of_week', plannerSettings.first_day_of_week);
    updateFieldError('mode', plannerSettings.mode);
  }, [plannerSettings.type]);
  const selectedDistributionName = useSelector(
    (state) => state.distribution_data.selected_entities.distribution?.name,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );
  const startWithMonday = shouldWeekStartWithMonday(
    selectedFarmingUnit,
    selectedDistributionName,
  );

  const firstDayOptions = getFirstDayOptions(startWithMonday).map((day) => {
    const jsDayIndex = daysArray.indexOf(day);
    return {
      title: t('day_' + day),
      value: (jsDayIndex - 1 + 7) % 7,
    };
  });

  const [firstDay, setFirstDay] = useState(plannerSettings.first_day_of_week);
  const [seasonTarget, setSeasonTarget] = useState(
    plannerSettings.season_irrigation_target || 0,
  );
  const [mode, setMode] = useState(
    plannerSettings.mode || weeklyPlannerModes.INTERVAL,
  );

  // Handlers
  const updateFieldError = (field, value) => {
    if (
      originalPlannerSettings.type === plannerType.WEEKLY_RECOMMENDATION &&
      originalPlannerSettings[field] != value
    ) {
      setErrors([
        ...errors,
        {
          field,
          message: errorMessages[field],
        },
      ]);
    } else {
      setErrors(errors.filter((error) => error.field !== field));
    }
  };

  const onFirstDayChange = (val) => {
    setFirstDay(val);
    handlePlannerSettingsChange &&
      handlePlannerSettingsChange('first_day_of_week', val);
    updateFieldError('first_day_of_week', val);
  };

  const onSeasonTargetChange = (e) => {
    const val = e.target.value.replace(/[^\d.]/g, '');
    setSeasonTarget(val);
    handlePlannerSettingsChange &&
      handlePlannerSettingsChange('season_irrigation_target', val);
  };

  const onModeChange = (val) => {
    setMode(val);
    handlePlannerSettingsChange && handlePlannerSettingsChange('mode', val);
    updateFieldError('mode', val);
  };

  return (
    <div
      className='d-flex flex-column align-items-start'
      style={{ padding: '16px 0 0 0' }}
    >
      <div className='d-flex flex-row align-items-start' style={{ gap: 16 }}>
        <FirstDaySelector
          firstDay={firstDay}
          onFirstDayChange={onFirstDayChange}
          firstDayOptions={firstDayOptions}
        />
        <div
          style={{ width: 1, background: 'var(--color-grey-91)', height: 84 }}
        />
        <SeasonTargetSelector
          seasonTarget={seasonTarget}
          onSeasonTargetChange={onSeasonTargetChange}
          userLengthUnits={userLengthUnits}
        />
      </div>
      <ModeSelector mode={mode} onModeChange={onModeChange} />
      <ErrorList errors={errors} style='justify-content-start' />
    </div>
  );
}

export const ErrorList = ({ errors, style = 'justify-content-end' }) => {
  const { t } = useTranslation();
  errors = removeDuplicateValues(errors);
  return (
    <div className={`d-flex flex-column ${style}`}>
      {errors &&
        Object.keys(errors).length > 0 &&
        Object.values(errors).map((err, i) => {
          return (
            <div className={`d-flex flex-row ${style}`} key={i}>
              <div className='tfedit-error'>
                <span className='tfedit-error-icon' />
                {t(err.message)}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export function RelatedSettings({
  fieldId,
  currentPlannerType,
  is_dynamic_planner_active,
  handleSettingsChange,
  userLengthUnits,
  plannerSettings,
  handlePlannerSettingsChange,
}) {
  const [originalPlannerSettings, setOriginalPlannerSettings] = useState({});
  useEffect(() => {
    setOriginalPlannerSettings({ ...plannerSettings });
  }, [fieldId]);

  switch (currentPlannerType) {
    case plannerType.DISABLED:
      return null;
    case plannerType.DAILY_RECOMMENDATION:
      return (
        <ByDaysPlannerSettings
          is_dynamic_planner_active={is_dynamic_planner_active}
          handleSettingsChange={handleSettingsChange}
        />
      );
    case plannerType.WEEKLY_RECOMMENDATION:
      return (
        <ByWeeksPlannerSettings
          plannerSettings={plannerSettings}
          userLengthUnits={userLengthUnits}
          handlePlannerSettingsChange={handlePlannerSettingsChange}
          originalPlannerSettings={originalPlannerSettings}
        />
      );
  }
  return null;
}
